import { Plus, Check, X, Sparkles, ShieldCheck, PackageCheck, PackageX } from "lucide-react";
import { eur } from "./format";
import { useT } from "./i18n";

function AddonCard({ addon, selected, onToggle, disabled }) {
  const { t } = useT();
  const showRegular = addon.regular_price > 0 && addon.addon_price > 0 && addon.regular_price !== addon.addon_price;
  const canSelect = addon.available && !disabled;
  return (
    <div
      className={`rounded-xl border p-4 flex gap-3 transition-colors ${selected ? "border-[#6E8B3D] bg-[#F7FAF0]" : "border-[#E5E5E0] bg-white"} ${!addon.available ? "opacity-70" : ""}`}
      data-testid={`addon-card-${addon.id}`}
    >
      {addon.image_url && (
        <img src={addon.image_url} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0 border border-[#EEEEEA]" />
      )}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-[#111110]">{addon.title}</div>
        {addon.description && <p className="text-xs text-[#525252] mt-0.5 leading-relaxed">{addon.description}</p>}
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <div className="flex items-baseline gap-2">
            {showRegular && <span className="font-mono text-xs text-[#8A9098] line-through">{eur(addon.regular_price)}</span>}
            <span className="font-display font-black text-base text-[#3F5222]" data-testid={`addon-price-${addon.id}`}>{eur(addon.price)}</span>
          </div>
          {addon.warranty_months > 0 && (
            <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#525252]">
              <ShieldCheck size={11} /> {t("months", { n: addon.warranty_months })}
            </span>
          )}
          <span className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest ${addon.available ? "text-[#3F5222]" : "text-[#9B2C1F]"}`} data-testid={`addon-availability-${addon.id}`}>
            {addon.available ? <><PackageCheck size={11} /> {t("available")}</> : <><PackageX size={11} /> {t("unavailable")}</>}
          </span>
        </div>
        <div className="mt-3">
          {selected ? (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#6E8B3D] text-white text-xs font-semibold" data-testid={`addon-added-${addon.id}`}>
                <Check size={13} /> {t("added")}
              </span>
              <button type="button" onClick={() => onToggle(addon)} disabled={disabled}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-[#E5E5E0] text-xs font-semibold text-[#525252] hover:bg-[#F0F0EB] min-h-[40px]"
                data-testid={`addon-remove-${addon.id}`}>
                <X size={13} /> {t("remove")}
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => onToggle(addon)} disabled={!canSelect}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[#111110] text-white text-xs font-semibold hover:bg-[#6E8B3D] disabled:opacity-40 disabled:cursor-not-allowed min-h-[40px]"
              data-testid={`addon-add-${addon.id}`}>
              <Plus size={13} /> {t("add")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AddonsSection({ addons, selectedIds, onToggle, disabled }) {
  const { t } = useT();
  if (!addons.length) return null;
  return (
    <div className="ds-card p-5" data-testid="quote-addons">
      <div className="flex items-center gap-2">
        <Sparkles size={15} className="text-[#6E8B3D]" />
        <h2 className="font-display font-bold text-base md:text-lg">{t("addons_title")}</h2>
      </div>
      <p className="text-xs text-[#525252] mt-0.5">{t("addons_intro")}</p>
      <div className="mt-3 space-y-3">
        {addons.map((a) => (
          <AddonCard key={a.id} addon={a} selected={selectedIds.includes(a.id)} onToggle={onToggle} disabled={disabled} />
        ))}
      </div>
    </div>
  );
}
